import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLegal } from '../context/LegalContext';
import SignedDocumentActions from '../components/legal/SignedDocumentActions';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Container,
    Divider,
    InputAdornment,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import PanToolAltOutlinedIcon from '@mui/icons-material/PanToolAltOutlined';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';

const normalizeSlug = (s) => {
    if (!s) return s;
    return String(s).replaceAll('_', '-');
};

const defaultGovernanceSlugCandidates = [
    'integrity-program-governance-policy',
    'integrity-program-governance',
    'integrity-program',
    'programa-de-integridade'
];
const SIDEBAR_DRAG_HINT_KEY = 'governance_sidebar_drag_hint_seen_v1';

const pickDefaultPolicy = (allPolicies = []) => {
    if (!allPolicies.length) return null;

    const bySlug = allPolicies.find((p) => defaultGovernanceSlugCandidates.includes(normalizeSlug(p?.slug)));
    if (bySlug) return bySlug;

    const byTitle = allPolicies.find((p) =>
        String(p?.title || '').toLowerCase().includes('programa de integridade')
    );
    if (byTitle) return byTitle;

    const byCategory = allPolicies.find((p) => p?.category === 'governance');
    if (byCategory) return byCategory;

    return allPolicies[0];
};

const scrollReaderToTop = () => {
    const root = document.querySelector('.scroller');
    if (root && typeof root.scrollTo === 'function') {
        root.scrollTo({ top: 0, behavior: 'auto' });
        return;
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
};

const isFragmentParagraph = (text = '') => {
    const clean = String(text).replace(/\s+/g, ' ').trim();
    if (!clean) return false;
    const words = clean.split(' ').filter(Boolean);
    return words.length <= 3 && clean.length <= 40;
};

const endsSentence = (text = '') => {
    const clean = String(text).trim();
    if (/\b(?:art|inc|n)\.$/i.test(clean)) return false;
    return /[.!?;:]$/.test(clean);
};

const normalizeInlineText = (text = '') => String(text).replace(/\s+/g, ' ').trim();

const startsWithLowercase = (text = '') => /^[a-záàâãéêíóôõúç]/.test(normalizeInlineText(text));

const splitLeadingContinuation = (text = '') => {
    const clean = normalizeInlineText(text);
    const match = clean.match(/^(.+?)\s+(\d+(?:\.\d+)*\.\s+.*)$/);

    if (!match) return null;

    return {
        head: match[1],
        tail: match[2]
    };
};

const splitSectionHeading = (text = '') => {
    const clean = normalizeInlineText(text);
    const match = clean.match(/^(\d+\.\s+.+?)\s+(\d+(?:\.\d+)+\.?\s+.*)$/);

    if (!match) return null;

    return {
        heading: match[1],
        body: match[2]
    };
};

const splitDocumentTitleIntro = (text = '') => {
    const clean = normalizeInlineText(text);
    const match = clean.match(/^([A-ZÀ-ÖØ-Þ0-9 ,./ºª&()–—-]{12,})\s+(1\.\s+.+?)\s+(1\.1\.\s+.*)$/);

    if (!match) return null;

    return {
        heading: match[2],
        body: match[3]
    };
};

const standaloneGovernanceHeadings = [
    'Princípio da Integridade',
    'Princípio do Respeito entre as Pessoas',
    'Princípio da Transparência e Clareza de Posições',
    'Princípio da Eficiência',
    'Princípio do Profissionalismo'
];

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const splitStandaloneGovernanceHeadings = (text = '') => {
    const clean = normalizeInlineText(text);

    for (const heading of standaloneGovernanceHeadings) {
        const pattern = new RegExp(`(.+[.!?;:])\\s+(${escapeRegExp(heading)}(?:\\s+.*)?)$`);
        const match = clean.match(pattern);

        if (match) {
            return [match[1], match[2]];
        }
    }

    return null;
};

const startsWithReadableText = (text = '') => /^[A-ZÀ-ÖØ-Þ0-9]/.test(normalizeInlineText(text));

const splitNumberedGovernanceMarkers = (text = '') => {
    const clean = normalizeInlineText(text);
    const markerRegex = /\s+(\d+(?:\.\d+)+\.?\s+)/g;
    const splitPoints = [];
    let match;

    while ((match = markerRegex.exec(clean)) !== null) {
        const markerStart = match.index + match[0].indexOf(match[1]);
        const before = clean.slice(0, markerStart).trim();
        const after = clean.slice(markerStart + match[1].length).trim();

        if (!before || !after) continue;
        if (!startsWithReadableText(after)) continue;
        splitPoints.push(markerStart);
    }

    if (!splitPoints.length) return null;

    const parts = [];
    let start = 0;

    splitPoints.forEach((point) => {
        const part = clean.slice(start, point).trim();
        if (part) parts.push(part);
        start = point;
    });

    const tail = clean.slice(start).trim();
    if (tail) parts.push(tail);

    return parts.length > 1 ? parts : null;
};

const splitTopLevelGovernanceHeadings = (text = '') => {
    const clean = normalizeInlineText(text);
    const markerRegex = /\s+(\d+\s+[A-ZÀ-ÖØ-Þ][A-ZÀ-ÖØ-Þ0-9\s,&()/-]{4,})(?=\s+\d+(?:\.\d+)+\.?\s+|$)/g;
    const splitPoints = [];
    let match;

    while ((match = markerRegex.exec(clean)) !== null) {
        const markerStart = match.index + match[0].indexOf(match[1]);
        const before = clean.slice(0, markerStart).trim();

        if (!before) continue;
        splitPoints.push(markerStart);
    }

    if (!splitPoints.length) return null;

    const parts = [];
    let start = 0;

    splitPoints.forEach((point) => {
        const part = clean.slice(start, point).trim();
        if (part) parts.push(part);
        start = point;
    });

    const tail = clean.slice(start).trim();
    if (tail) parts.push(tail);

    return parts.length > 1 ? parts : null;
};
const splitImportedParagraphText = (text = '') => {
    const clean = normalizeInlineText(text);
    if (!clean) return [];

    const initialSplit = splitDocumentTitleIntro(clean) || splitSectionHeading(clean);
    let parts = initialSplit ? [initialSplit.heading, initialSplit.body] : [clean];
    let changed = true;

    while (changed) {
        changed = false;
        const nextParts = [];

        for (const part of parts) {
            const standaloneSplit = splitStandaloneGovernanceHeadings(part);
            const topLevelSplit = !standaloneSplit ? splitTopLevelGovernanceHeadings(part) : null;
            const numberedSplit = !standaloneSplit && !topLevelSplit ? splitNumberedGovernanceMarkers(part) : null;
            const split = standaloneSplit || topLevelSplit || numberedSplit;

            if (split && split.length > 1) {
                nextParts.push(...split);
                changed = true;
                continue;
            }

            nextParts.push(part);
        }

        parts = nextParts;
    }
    return parts.map(normalizeInlineText).filter(Boolean);
};
const removeSignatureArtifacts = (text = '') => {
    return normalizeInlineText(text)
        .replace(/Parque Tecnol[\s\S]*?Documento assinado eletronicamente, conforme MP 2\.200-2\/01, Art\. 10.?o, .2\. Brasil\s*/gi, '')
        .replace(/D4Sign\s+[a-f0-9-]{20,}[\s\S]*?Documento assinado eletronicamente, conforme MP 2\.200-2\/01, Art\. 10.?o, .2\. Brasil\s*/gi, '')
        .replace(/\d+\s+p.ginas - Datas[\s\S]*?Assinaturas eletr.nicas e f.sicas t.m igual validade legal, conforme MP 2\.200-2\/2001 e Lei 14\.063\/2020\.\s*/gi, '')
        .replace(/\s*Bras.lia,\s*data eletr.nica[\s\S]*$/i, '')
        .replace(/^Parque Tecnol[\s\S]*$/i, '')
        .replace(/^Asa Norte,[\s\S]*$/i, '')
        .replace(/^comercial@r5id\.com\.br[\s\S]*$/i, '')
        .trim();
};
const isLegalSignatureArtifact = (text = '') => {
    const clean = normalizeInlineText(text);
    return (
        (clean.includes('Bras') && clean.includes('data eletr')) ||
        clean.includes('FABIO JACINTO BARRETO DE SOUZA') ||
        (clean.includes('Parque Tecnol') && clean.includes('UnB')) ||
        (clean.includes('Asa Norte, Bras') && clean.includes('CEP')) ||
        clean.includes('comercial@r5id.com.br') ||
        clean.includes('D4Sign') ||
        clean.includes('Documento assinado') ||
        clean.includes('secure.d4sign.com.br/verificar') ||
        (/^\d+\s+p/.test(clean) && clean.includes('Datas')) ||
        (clean.includes('Assinaturas eletr') && clean.includes('validade legal'))
    );
};
const removeLegalSignatureArtifacts = (root) => {
    Array.from(root.querySelectorAll('p')).forEach((paragraph) => {
        const cleaned = removeSignatureArtifacts(paragraph.textContent || '');

        if (!cleaned || isLegalSignatureArtifact(cleaned)) {
            paragraph.remove();
            return;
        }

        paragraph.textContent = cleaned;
    });
};

const removeSummaryArtifacts = (root) => {
    Array.from(root.querySelectorAll('p')).forEach((paragraph) => {
        const text = normalizeInlineText(paragraph.textContent || '');
        if (!/^SUMÁRIO\b/i.test(text)) return;

        const trailingHeading = text.match(/(\d+\.\s+[A-ZÁÀÂÃÉÊÍÓÔÕÚÇ][A-ZÁÀÂÃÉÊÍÓÔÕÚÇ0-9\s,&()/-]{5,})$/);

        if (trailingHeading) {
            paragraph.textContent = trailingHeading[1];
            return;
        }

        paragraph.remove();
    });
};

const splitMergedSectionHeadings = (root) => {
    Array.from(root.querySelectorAll('p')).forEach((paragraph) => {
        const parts = splitImportedParagraphText(paragraph.textContent || '');
        if (parts.length < 2) return;

        paragraph.textContent = parts[0];

        parts.slice(1).reverse().forEach((part) => {
            const body = document.createElement('p');
            body.textContent = part;
            paragraph.insertAdjacentElement('afterend', body);
        });
    });
};
const repairFragmentedParagraphs = (html = '') => {
    if (!html || typeof document === 'undefined') return html;

    const template = document.createElement('template');
    template.innerHTML = html;
    removeLegalSignatureArtifacts(template.content);
    removeSummaryArtifacts(template.content);

    const parents = Array.from(template.content.querySelectorAll('article, section, div'));
    const roots = parents.length ? parents : [template.content];

    roots.forEach((parent) => {
        const children = Array.from(parent.children || []);
        let index = 0;

        while (index < children.length) {
            const child = children[index];
            const text = child?.textContent || '';
            const previousChild = child?.previousElementSibling;
            const childContinuation = child?.tagName === 'P' ? splitLeadingContinuation(text) : null;

            if (
                childContinuation &&
                previousChild?.tagName === 'P' &&
                !previousChild.classList.contains('last-updated') &&
                !endsSentence(previousChild.textContent)
            ) {
                previousChild.textContent = `${normalizeInlineText(previousChild.textContent)} ${childContinuation.head}`;
                child.textContent = childContinuation.tail;
                index += 1;
                continue;
            }

            if (
                child?.tagName === 'P' &&
                previousChild?.tagName === 'P' &&
                !previousChild.classList.contains('last-updated') &&
                !endsSentence(previousChild.textContent) &&
                !/^\d+(?:\.\d+)*\.\s+/.test(normalizeInlineText(text))
            ) {
                previousChild.textContent = `${normalizeInlineText(previousChild.textContent)} ${normalizeInlineText(text)}`;
                child.remove();
                index += 1;
                continue;
            }

            if (
                child?.tagName === 'P' &&
                startsWithLowercase(text) &&
                previousChild?.tagName === 'P' &&
                !previousChild.classList.contains('last-updated')
            ) {
                previousChild.textContent = `${normalizeInlineText(previousChild.textContent)} ${normalizeInlineText(text)}`;
                child.remove();
                index += 1;
                continue;
            }

            if (child?.tagName !== 'P' || !isFragmentParagraph(text)) {
                index += 1;
                continue;
            }

            const run = [];
            let cursor = index;

            while (
                cursor < children.length &&
                children[cursor]?.tagName === 'P' &&
                isFragmentParagraph(children[cursor].textContent || '')
            ) {
                run.push(children[cursor]);
                cursor += 1;
            }

            const previous = run[0].previousElementSibling;
            const canContinuePrevious =
                previous?.tagName === 'P' &&
                !previous.classList.contains('last-updated') &&
                !endsSentence(previous.textContent);

            if (run.length < 4 && !canContinuePrevious) {
                index = cursor;
                continue;
            }

            const joinedText = run
                .map((node) => normalizeInlineText(node.textContent || ''))
                .filter(Boolean)
                .join(' ');

            if (canContinuePrevious) {
                previous.textContent = `${normalizeInlineText(previous.textContent)} ${joinedText}`;
                run.forEach((node) => node.remove());

                const next = children[cursor];
                const continuation = next?.tagName === 'P' ? splitLeadingContinuation(next.textContent) : null;

                if (!endsSentence(previous.textContent) && continuation) {
                    previous.textContent = `${normalizeInlineText(previous.textContent)} ${continuation.head}`;
                    next.textContent = continuation.tail;
                }
            } else {
                run[0].textContent = joinedText;
                run.slice(1).forEach((node) => node.remove());
            }

            index = cursor;
        }
    });

    splitMergedSectionHeadings(template.content);
    removeLegalSignatureArtifacts(template.content);

    return template.innerHTML;
};

const GovernanceReaderPage = () => {
    const { slug } = useParams();
    const { documents = [], loading } = useLegal();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchQuery, setSearchQuery] = useState('');
    const [sidebarYOffset, setSidebarYOffset] = useState(0);
    const [isSidebarDragging, setIsSidebarDragging] = useState(false);
    const [showDragHint, setShowDragHint] = useState(() => {
        if (typeof window === 'undefined') return true;
        return window.localStorage.getItem(SIDEBAR_DRAG_HINT_KEY) !== '1';
    });
    const sidebarDragRef = useRef({ startY: 0, startOffset: 0, moved: false });
    const sidebarListRef = useRef(null);
    const sidebarScrollTrackRef = useRef(null);
    const sidebarThumbDragRef = useRef({ startY: 0, startScrollTop: 0 });
    const [sidebarScrollMetrics, setSidebarScrollMetrics] = useState({
        scrollTop: 0,
        scrollHeight: 1,
        clientHeight: 1
    });
    const [isScrollbarThumbDragging, setIsScrollbarThumbDragging] = useState(false);
    const palette = {
        red: '#CC0000',
        bg: '#060606',
        bgSoft: '#0f1012',
        border: '#23252a',
        text: '#d2d4d8',
        heading: '#f5f5f6',
        muted: '#9b9da3'
    };
    const ds = {
        pageBg: '#f5f6f8',
        surface: '#ffffff',
        border: alpha(theme.palette.secondary.main, 0.14),
        borderStrong: alpha(theme.palette.secondary.main, 0.24),
        textPrimary: theme.palette.text.primary,
        textSecondary: alpha(theme.palette.text.primary, 0.68),
        textMuted: alpha(theme.palette.text.primary, 0.56),
        selectedBg: alpha(theme.palette.secondary.main, 0.06),
        cardRadius: `${theme.shape.borderRadius + 8}px`,
        panelRadius: `${theme.shape.borderRadius + 4}px`
    };
    const commandButtonSx = {
        borderRadius: '14px',
        borderWidth: '1px',
        borderColor: palette.border,
        color: palette.text,
        textTransform: 'uppercase',
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600,
        letterSpacing: '0.08em',
        fontSize: '0.76rem',
        px: 2,
        minHeight: 42,
        '&:hover': { borderColor: palette.red, bgcolor: 'rgba(204,0,0,0.08)' },
        '&.Mui-disabled': { borderColor: '#1a1b1f', color: '#5b5e66' }
    };

    const policies = (documents || [])
        .filter(doc => doc && doc.published !== false)
        .map(doc => ({ ...doc }));

    const slugKey = normalizeSlug(slug);

    const currentPolicy =
        policies.find(p => normalizeSlug(p.slug) === slugKey) ||
        policies.find(p => String(p.id) === String(slug));
    const repairedPolicyContent = useMemo(
        () => repairFragmentedParagraphs(currentPolicy?.content || ''),
        [currentPolicy?.content]
    );

    const siteDocs = policies.filter(p => !p.category || p.category === 'legal');
    const institutionalDocs = policies.filter(p => p.category === 'governance');
    const orderedPolicies = [...siteDocs, ...institutionalDocs];

    const normalizedQuery = String(searchQuery || '').trim().toLowerCase();
    const policyMatchesQuery = (policy) => {
        if (!normalizedQuery) return true;
        const haystack = [policy?.title, policy?.slug, policy?.category]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        return haystack.includes(normalizedQuery);
    };

    const filteredSiteDocs = siteDocs.filter(policyMatchesQuery);
    const filteredInstitutionalDocs = institutionalDocs.filter(policyMatchesQuery);

    const currentIndex = orderedPolicies.findIndex((p) => {
        const pSlug = normalizeSlug(p.slug);
        if (pSlug && slugKey) return pSlug === slugKey;
        return String(p.id) === String(slug);
    });

    const previousPolicy = currentIndex > 0 ? orderedPolicies[currentIndex - 1] : null;
    const nextPolicy = currentIndex >= 0 && currentIndex < orderedPolicies.length - 1
        ? orderedPolicies[currentIndex + 1]
        : null;

    useEffect(() => {
        if (!slug && policies.length > 0) {
            const first = pickDefaultPolicy(policies);
            navigate(`/governanca/${first.slug || first.id}`, { replace: true });
            return;
        }
        if (slug) scrollReaderToTop();
    }, [slug, policies, navigate]);

    const selectorValue = useMemo(() => {
        if (!currentPolicy) return '';
        return normalizeSlug(currentPolicy.slug) || String(currentPolicy.id);
    }, [currentPolicy]);

    const updateSidebarScrollMetrics = useCallback(() => {
        const node = sidebarListRef.current;
        if (!node) return;

        setSidebarScrollMetrics({
            scrollTop: node.scrollTop,
            scrollHeight: node.scrollHeight || 1,
            clientHeight: node.clientHeight || 1
        });
    }, []);

    const scrollSidebarBy = useCallback((delta) => {
        const node = sidebarListRef.current;
        if (!node) return;
        node.scrollBy({ top: delta, behavior: 'smooth' });
    }, []);

    const sidebarScrollMax = Math.max(0, sidebarScrollMetrics.scrollHeight - sidebarScrollMetrics.clientHeight);
    const sidebarThumbHeightPct = sidebarScrollMax > 0
        ? Math.max(18, Math.min(100, (sidebarScrollMetrics.clientHeight / sidebarScrollMetrics.scrollHeight) * 100))
        : 100;
    const sidebarThumbTopPct = sidebarScrollMax > 0
        ? (sidebarScrollMetrics.scrollTop / sidebarScrollMax) * (100 - sidebarThumbHeightPct)
        : 0;

    const startSidebarDrag = useCallback((event) => {
        if (typeof event.button === 'number' && event.button !== 0) return;
        if (typeof event.clientY !== 'number') return;

        sidebarDragRef.current = {
            startY: event.clientY,
            startOffset: sidebarYOffset,
            moved: false
        };
        setIsSidebarDragging(true);
    }, [sidebarYOffset]);

    useEffect(() => {
        if (!isSidebarDragging) return undefined;

        const onPointerMove = (event) => {
            const delta = event.clientY - sidebarDragRef.current.startY;
            if (Math.abs(delta) > 4) sidebarDragRef.current.moved = true;
            const next = Math.max(-180, Math.min(220, sidebarDragRef.current.startOffset + delta));
            setSidebarYOffset(next);
        };

        const stopDrag = () => {
            const hasMoved = sidebarDragRef.current.moved;
            setIsSidebarDragging(false);
            if (hasMoved && showDragHint) {
                setShowDragHint(false);
                if (typeof window !== 'undefined') {
                    window.localStorage.setItem(SIDEBAR_DRAG_HINT_KEY, '1');
                }
            }
            setTimeout(() => {
                sidebarDragRef.current.moved = false;
            }, 0);
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', stopDrag);
        window.addEventListener('pointercancel', stopDrag);

        const prevCursor = document.body.style.cursor;
        const prevSelect = document.body.style.userSelect;
        document.body.style.cursor = 'grab';
        document.body.style.userSelect = 'none';

        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', stopDrag);
            window.removeEventListener('pointercancel', stopDrag);
            document.body.style.cursor = prevCursor;
            document.body.style.userSelect = prevSelect;
        };
    }, [isSidebarDragging, showDragHint]);

    useEffect(() => {
        const rafId = window.requestAnimationFrame(updateSidebarScrollMetrics);
        return () => window.cancelAnimationFrame(rafId);
    }, [
        updateSidebarScrollMetrics,
        filteredSiteDocs.length,
        filteredInstitutionalDocs.length,
        selectorValue,
        searchQuery
    ]);

    useEffect(() => {
        window.addEventListener('resize', updateSidebarScrollMetrics);
        return () => window.removeEventListener('resize', updateSidebarScrollMetrics);
    }, [updateSidebarScrollMetrics]);

    const startScrollbarThumbDrag = useCallback((event) => {
        if (sidebarScrollMax <= 0) return;
        event.preventDefault();
        event.stopPropagation();

        sidebarThumbDragRef.current = {
            startY: event.clientY,
            startScrollTop: sidebarListRef.current?.scrollTop || 0
        };
        setIsScrollbarThumbDragging(true);
    }, [sidebarScrollMax]);

    const scrollSidebarToTrackPosition = useCallback((clientY) => {
        const listNode = sidebarListRef.current;
        const trackNode = sidebarScrollTrackRef.current;
        if (!listNode || !trackNode || sidebarScrollMax <= 0) return;

        const rect = trackNode.getBoundingClientRect();
        const thumbHeightPx = (sidebarThumbHeightPct / 100) * rect.height;
        const maxThumbTravel = Math.max(1, rect.height - thumbHeightPx);
        const pointerY = clientY - rect.top;
        const targetThumbTop = Math.max(0, Math.min(maxThumbTravel, pointerY - thumbHeightPx / 2));
        const nextScrollTop = (targetThumbTop / maxThumbTravel) * sidebarScrollMax;
        listNode.scrollTop = nextScrollTop;
    }, [sidebarScrollMax, sidebarThumbHeightPct]);

    useEffect(() => {
        if (!isScrollbarThumbDragging) return undefined;

        const onPointerMove = (event) => {
            const listNode = sidebarListRef.current;
            const trackNode = sidebarScrollTrackRef.current;
            if (!listNode || !trackNode || sidebarScrollMax <= 0) return;

            const rect = trackNode.getBoundingClientRect();
            const thumbHeightPx = (sidebarThumbHeightPct / 100) * rect.height;
            const maxThumbTravel = Math.max(1, rect.height - thumbHeightPx);
            const deltaY = event.clientY - sidebarThumbDragRef.current.startY;
            const scrollDelta = (deltaY / maxThumbTravel) * sidebarScrollMax;
            const nextScrollTop = Math.max(
                0,
                Math.min(sidebarScrollMax, sidebarThumbDragRef.current.startScrollTop + scrollDelta)
            );
            listNode.scrollTop = nextScrollTop;
        };

        const stopDragging = () => setIsScrollbarThumbDragging(false);

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);

        const prevCursor = document.body.style.cursor;
        const prevSelect = document.body.style.userSelect;
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';

        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
            document.body.style.cursor = prevCursor;
            document.body.style.userSelect = prevSelect;
        };
    }, [isScrollbarThumbDragging, sidebarScrollMax, sidebarThumbHeightPct]);

    if (loading && !currentPolicy) {
        return (
            <Box sx={{ minHeight: 'var(--page-h)', display: 'grid', placeItems: 'center', bgcolor: palette.bg }}>
                <Typography sx={{ color: palette.text, letterSpacing: '0.06em', fontWeight: 600 }}>
                    Carregando documentos...
                </Typography>
            </Box>
        );
    }

    if (!currentPolicy) {
        return (
            <Box sx={{ minHeight: 'var(--page-h)', display: 'grid', placeItems: 'center', bgcolor: palette.bg }}>
                <Stack spacing={2} alignItems="center">
                    <Typography sx={{ color: palette.heading, fontWeight: 700 }}>Documento não encontrado</Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/governanca')}
                        sx={{ ...commandButtonSx }}
                    >
                        Voltar
                    </Button>
                </Stack>
            </Box>
        );
    }

    const goToPolicy = (policy) => {
        if (!policy) return;
        navigate(`/governanca/${policy.slug || policy.id}`);
    };

    const headerPanel = (
        <Box
            sx={{
                p: { xs: 2, md: 3 },
                border: `1px solid ${palette.border}`,
                bgcolor: 'rgba(10,10,10,0.88)',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '2px',
                    bgcolor: palette.red
                }}
            />
            <Typography
                variant="overline"
                sx={{
                    color: palette.red,
                    fontFamily: '"JetBrains Mono", monospace',
                    letterSpacing: '0.16em',
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.8
                }}
            >
                R5 // PROGRAMA DE INTEGRIDADE
            </Typography>
            <Typography
                variant="h1"
                sx={{
                    fontSize: { xs: '1.7rem', md: '2.3rem' },
                    lineHeight: 1.08,
                    letterSpacing: '-0.02em',
                    color: palette.heading,
                    fontWeight: 800
                }}
            >
                Governança e Compliance
                <Box component="span" sx={{ color: palette.red }}>.</Box>
            </Typography>
            <Typography
                sx={{
                    mt: 1.2,
                    color: palette.muted,
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    maxWidth: 820
                }}
            >
                Leitura oficial dos documentos institucionais com navegação contínua.
            </Typography>
        </Box>
    );

    const readerCard = (
        <Box
            sx={{
                bgcolor: palette.bgSoft,
                border: `1px solid ${palette.border}`,
                borderRadius: '14px',
                p: { xs: 3, md: 6 },
                boxShadow: '0 18px 60px rgba(0,0,0,0.45)'
            }}
        >
            <Typography
                sx={{
                    color: palette.red,
                    fontFamily: '"JetBrains Mono", monospace',
                    letterSpacing: '0.12em',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    mb: 1.2
                }}
            >
                DOC_ATUAL
            </Typography>
            <Typography
                variant="h1"
                sx={{
                    fontSize: { xs: '1.8rem', md: '2.6rem' },
                    fontWeight: 800,
                    color: palette.heading,
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    mb: 2
                }}
            >
                {currentPolicy.title}
            </Typography>

            <Divider sx={{ mb: 4, borderColor: '#2b2e34' }} />

            <Box
                className="governance-reading-content"
                sx={{
                    color: palette.text,
                    '& h1': {
                        display: 'none'
                    },
                    '& h2': {
                        fontSize: { xs: '1.1rem', md: '1.35rem' },
                        color: palette.heading,
                        mt: 4,
                        mb: 1.5,
                        fontWeight: 700,
                        paddingLeft: '0.7rem',
                        borderLeft: `2px solid ${palette.red}`
                    },
                    '& h3': {
                        fontSize: { xs: '1rem', md: '1.15rem' },
                        color: palette.heading,
                        mt: 3,
                        mb: 1,
                        fontWeight: 700
                    },
                    '& p': {
                        fontSize: { xs: '1rem', md: '1.08rem' },
                        lineHeight: 1.9,
                        color: palette.text,
                        mb: 2,
                        textAlign: 'left'
                    },
                    '& ul, & ol': {
                        pl: 3,
                        mb: 2
                    },
                    '& li': {
                        fontSize: { xs: '1rem', md: '1.05rem' },
                        lineHeight: 1.8,
                        color: palette.text,
                        mb: 1
                    },
                    '& strong, & b': {
                        color: '#f1f1f2'
                    },
                    '& .last-updated': {
                        fontSize: '0.82rem',
                        color: palette.muted,
                        letterSpacing: '0.04em',
                        mb: 2
                    },
                    '& a': {
                        color: '#efefef',
                        textDecoration: 'underline',
                        textDecorationColor: '#6a6c72',
                        '&:hover': {
                            color: '#ffffff',
                            textDecorationColor: palette.red
                        }
                    }
                }}
            >
                <Box dangerouslySetInnerHTML={{ __html: repairedPolicyContent }} />
            </Box>
            <SignedDocumentActions slug={currentPolicy.slug} />
        </Box>
    );

    const mobilePolicyAccordion = (
        <Accordion
            defaultExpanded
            disableGutters
            sx={{
                bgcolor: 'rgba(8,8,9,0.8)',
                border: `1px solid ${palette.border}`,
                borderRadius: '14px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)'
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: palette.muted }} />}
                aria-controls="governance-policy-list"
                id="governance-policy-accordion"
                sx={{
                    px: 3,
                    py: 1.5,
                    borderBottom: `1px solid ${palette.border}`,
                    '& .MuiAccordionSummary-content': {
                        margin: 0
                    }
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        letterSpacing: '0.08em',
                        color: palette.heading,
                        fontWeight: 600
                    }}
                >
                    Documentos disponíveis
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <List disablePadding>
                    {orderedPolicies.map((policy) => {
                        const policyValue = normalizeSlug(policy.slug) || String(policy.id);
                        const isSelected = policyValue === selectorValue;
                        return (
                            <ListItemButton
                                key={policyValue}
                                onClick={() => goToPolicy(policy)}
                                selected={isSelected}
                                sx={{
                                    px: 3,
                                    py: 1.2,
                                    borderBottom: `1px solid ${palette.border}`,
                                    '&:last-of-type': { borderBottom: 'none' },
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(204,0,0,0.08)',
                                        '& .MuiListItemText-primary': { color: palette.heading }
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={policy.title}
                                    secondary={policy.category === 'governance' ? 'Padrão corporativo' : 'Protocolo do site'}
                                    primaryTypographyProps={{ fontWeight: 600, color: palette.text }}
                                    secondaryTypographyProps={{ fontSize: '0.78rem', color: palette.muted }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </AccordionDetails>
        </Accordion>
    );

    const desktopNavigationButtons = (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.2}
            sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'center' }}
        >
            <Button
                variant="outlined"
                color="secondary"
                startIcon={<KeyboardArrowLeftIcon />}
                disabled={!previousPolicy}
                onClick={() => goToPolicy(previousPolicy)}
                sx={{
                    borderRadius: `${theme.shape.borderRadius}px`,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.4,
                    minWidth: 136,
                    minHeight: 41,
                    borderColor: ds.borderStrong,
                    '&:hover': { borderColor: theme.palette.secondary.main, bgcolor: alpha(theme.palette.secondary.main, 0.04) },
                    '&.Mui-disabled': { borderColor: ds.border, color: ds.textMuted }
                }}
            >
                Anterior
            </Button>
            <Button
                variant="outlined"
                color="secondary"
                endIcon={<KeyboardArrowRightIcon />}
                disabled={!nextPolicy}
                onClick={() => goToPolicy(nextPolicy)}
                sx={{
                    borderRadius: `${theme.shape.borderRadius}px`,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.4,
                    minWidth: 136,
                    minHeight: 41,
                    borderColor: ds.borderStrong,
                    '&:hover': { borderColor: theme.palette.secondary.main, bgcolor: alpha(theme.palette.secondary.main, 0.04) },
                    '&.Mui-disabled': { borderColor: ds.border, color: ds.textMuted }
                }}
            >
                Próximo
            </Button>
        </Stack>
    );

    if (isMobile) {
        return (
            <Box
                sx={{
                    minHeight: 'var(--page-h)',
                    bgcolor: palette.bg,
                    backgroundImage: `
                        linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px),
                        radial-gradient(circle at 15% 0%, rgba(204,0,0,0.14), transparent 35%)
                    `,
                    backgroundSize: '40px 40px, 40px 40px, auto'
                }}
            >
                <Container
                    maxWidth="md"
                    sx={{
                        pt: { xs: 14, md: 17 },
                        pb: { xs: 3, md: 4 },
                        minHeight: 'var(--page-h)'
                    }}
                >
                    <Stack spacing={2}>
                        {headerPanel}
                            <Stack spacing={1.2}>
                                {mobilePolicyAccordion}
                            </Stack>
                        {readerCard}
                        <Stack spacing={1.2}>
                            <Stack direction="row" spacing={1.2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<KeyboardArrowLeftIcon />}
                                    disabled={!previousPolicy}
                                    onClick={() => goToPolicy(previousPolicy)}
                                    sx={{
                                        ...commandButtonSx,
                                        width: '50%',
                                        minHeight: 48
                                    }}
                                >
                                    Anterior
                                </Button>
                                <Button
                                    variant="outlined"
                                    endIcon={<KeyboardArrowRightIcon />}
                                    disabled={!nextPolicy}
                                    onClick={() => goToPolicy(nextPolicy)}
                                    sx={{
                                        ...commandButtonSx,
                                        width: '50%',
                                        minHeight: 48
                                    }}
                                >
                                    Próximo
                                </Button>
                            </Stack>
                            <Typography sx={{ color: palette.muted, fontSize: '0.78rem', letterSpacing: '0.05em' }}>
                                Modo de leitura contínua
                            </Typography>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: 'var(--page-h)',
                bgcolor: ds.pageBg,
                backgroundImage: `
                    linear-gradient(${alpha(theme.palette.secondary.main, 0.04)} 1px, transparent 1px),
                    linear-gradient(90deg, ${alpha(theme.palette.secondary.main, 0.04)} 1px, transparent 1px)
                `,
                backgroundSize: '28px 28px'
            }}
        >
            <Container
                maxWidth="xl"
                sx={{
                    pt: { xs: 6.5, md: 7.2 },
                    pb: { xs: 4, md: 5 },
                    minHeight: 'var(--page-h)'
                }}
            >
                <Stack spacing={1.15}>
                    <Typography
                        variant="overline"
                        sx={{
                            display: 'block',
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            letterSpacing: '0.12em',
                            fontFamily: '"Inter", sans-serif'
                        }}
                    >
                        GOVERNANÇA
                    </Typography>
                    <Typography
                        variant="h1"
                        sx={{
                            textTransform: 'none',
                            fontSize: { xs: '2.45rem', md: '3.3rem', lg: '3.75rem' },
                            lineHeight: 1.02,
                            letterSpacing: '-0.032em',
                            fontWeight: 800,
                            color: ds.textPrimary,
                            maxWidth: '15ch',
                            fontFamily: '"Inter", sans-serif'
                        }}
                    >
                        Governança e Compliance
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: '1rem', md: '1.06rem' },
                            lineHeight: 1.55,
                            color: ds.textSecondary,
                            maxWidth: '64ch',
                            mt: 0.25
                        }}
                    >
                        Leitura institucional organizada por índice para consulta rápida
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        mt: 2.6,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '340px minmax(0, 1fr)' },
                        gap: 3.2,
                        alignItems: 'start'
                    }}
                >
                    <Box
                        sx={{
                            position: { xs: 'static', lg: 'sticky' },
                            top: { xs: 'auto', lg: 'calc(var(--header-h, 84px) + 20px)' }
                        }}
                    >
                        <Box
                            onPointerDown={startSidebarDrag}
                            onMouseDown={startSidebarDrag}
                            onClickCapture={(event) => {
                                if (sidebarDragRef.current.moved) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }
                            }}
                            sx={{
                                bgcolor: ds.surface,
                                border: `1px solid ${ds.border}`,
                                borderRadius: ds.panelRadius,
                                p: 2,
                                boxShadow: '0 10px 26px rgba(24,34,56,0.08)',
                                transform: `translateY(${sidebarYOffset}px)`,
                                transition: isSidebarDragging ? 'none' : 'transform 180ms ease',
                                cursor: isSidebarDragging ? 'grabbing' : 'grab',
                                touchAction: 'none'
                            }}
                        >
                            <Stack spacing={1.35}>
                                <Stack spacing={0.55} sx={{ px: 0.4, pt: 0.2 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: ds.textPrimary,
                                            lineHeight: 1.2,
                                            letterSpacing: '-0.01em',
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            fontFamily: '"Inter", sans-serif',
                                            textTransform: 'none'
                                        }}
                                    >
                                        Índice de Leitura
                                    </Typography>
                                    {showDragHint ? (
                                        <Stack
                                            direction="row"
                                            spacing={0.55}
                                            alignItems="center"
                                            sx={{
                                                px: 0.15,
                                                py: 0.2,
                                                opacity: 0.78
                                            }}
                                        >
                                            <PanToolAltOutlinedIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: alpha(theme.palette.secondary.main, 0.5)
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: '0.78rem',
                                                    lineHeight: 1.3,
                                                    color: alpha(theme.palette.text.primary, 0.54),
                                                    fontWeight: 450,
                                                    letterSpacing: '0.002em'
                                                }}
                                            >
                                                Arraste para posicionar este card.
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontSize: '0.8rem',
                                                lineHeight: 1.35,
                                                color: alpha(theme.palette.text.primary, 0.4)
                                            }}
                                        >
                                            Posição ajustável.
                                        </Typography>
                                    )}
                                </Stack>

                                <TextField
                                    size="small"
                                    placeholder="Buscar no índice"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#697383', fontSize: 19 }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: `${theme.shape.borderRadius}px`,
                                            bgcolor: alpha(theme.palette.background.default, 0.7),
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.93rem',
                                                fontWeight: 430,
                                                letterSpacing: '-0.002em',
                                                color: ds.textPrimary
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: ds.border
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: ds.borderStrong
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: alpha(theme.palette.text.primary, 0.34)
                                            }
                                        }
                                    }}
                                />

                                <Box sx={{ position: 'relative' }}>
                                    <List
                                        ref={sidebarListRef}
                                        onScroll={updateSidebarScrollMetrics}
                                        disablePadding
                                        sx={{
                                            border: `1px solid ${ds.border}`,
                                            borderRadius: `${theme.shape.borderRadius}px`,
                                            height: 'calc(var(--page-h) - 250px)',
                                            minHeight: '440px',
                                            maxHeight: 'calc(var(--page-h) - 220px)',
                                            overflowY: 'auto',
                                            pr: 3.3,
                                            bgcolor: alpha(theme.palette.background.default, 0.8),
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            '&::-webkit-scrollbar': { width: 0, height: 0 }
                                        }}
                                    >
                                        <ListSubheader
                                            disableSticky
                                            sx={{
                                                bgcolor: alpha(theme.palette.secondary.main, 0.04),
                                                color: ds.textMuted,
                                                fontWeight: 620,
                                                letterSpacing: '0.06em',
                                                textTransform: 'uppercase',
                                                fontSize: '0.7rem',
                                                lineHeight: 1.2,
                                                py: 1
                                            }}
                                        >
                                            Documentos do site
                                        </ListSubheader>
                                        {filteredSiteDocs.map((policy) => {
                                            const policyValue = normalizeSlug(policy.slug) || String(policy.id);
                                            const isSelected = policyValue === selectorValue;
                                            return (
                                                <ListItemButton
                                                    key={policyValue}
                                                    onClick={() => goToPolicy(policy)}
                                                    selected={isSelected}
                                                    sx={{
                                                        py: 1.06,
                                                        px: 1.35,
                                                        borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
                                                        position: 'relative',
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.text.primary, 0.045),
                                                            '& .MuiListItemText-primary': {
                                                                color: alpha(theme.palette.text.primary, 0.93)
                                                            }
                                                        },
                                                        '&.Mui-selected': {
                                                            bgcolor: ds.selectedBg,
                                                            '& .MuiListItemText-primary': { color: ds.textPrimary, fontWeight: 560 },
                                                            '&::before': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                left: 0,
                                                                top: 8,
                                                                bottom: 8,
                                                                width: 3,
                                                                borderRadius: '0 2px 2px 0',
                                                                bgcolor: alpha(theme.palette.text.primary, 0.24)
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={policy.title}
                                                        primaryTypographyProps={{
                                                            textTransform: 'none',
                                                            fontWeight: 430,
                                                            color: alpha(theme.palette.text.primary, 0.84),
                                                            lineHeight: 1.42,
                                                            fontSize: '0.9rem',
                                                            letterSpacing: '-0.002em'
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}

                                        <ListSubheader
                                            disableSticky
                                            sx={{
                                                bgcolor: alpha(theme.palette.secondary.main, 0.04),
                                                color: ds.textMuted,
                                                fontWeight: 620,
                                                letterSpacing: '0.06em',
                                                textTransform: 'uppercase',
                                                fontSize: '0.7rem',
                                                lineHeight: 1.2,
                                                py: 1
                                            }}
                                        >
                                            Padrões corporativos
                                        </ListSubheader>
                                        {filteredInstitutionalDocs.map((policy) => {
                                            const policyValue = normalizeSlug(policy.slug) || String(policy.id);
                                            const isSelected = policyValue === selectorValue;
                                            return (
                                                <ListItemButton
                                                    key={policyValue}
                                                    onClick={() => goToPolicy(policy)}
                                                    selected={isSelected}
                                                    sx={{
                                                        py: 1.06,
                                                        px: 1.35,
                                                        borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
                                                        '&:last-of-type': { borderBottom: 'none' },
                                                        position: 'relative',
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.text.primary, 0.045),
                                                            '& .MuiListItemText-primary': {
                                                                color: alpha(theme.palette.text.primary, 0.93)
                                                            }
                                                        },
                                                        '&.Mui-selected': {
                                                            bgcolor: ds.selectedBg,
                                                            '& .MuiListItemText-primary': { color: ds.textPrimary, fontWeight: 560 },
                                                            '&::before': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                left: 0,
                                                                top: 8,
                                                                bottom: 8,
                                                                width: 3,
                                                                borderRadius: '0 2px 2px 0',
                                                                bgcolor: alpha(theme.palette.text.primary, 0.24)
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={policy.title}
                                                        primaryTypographyProps={{
                                                            textTransform: 'none',
                                                            fontWeight: 430,
                                                            color: alpha(theme.palette.text.primary, 0.84),
                                                            lineHeight: 1.42,
                                                            fontSize: '0.9rem',
                                                            letterSpacing: '-0.002em'
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                        {filteredSiteDocs.length === 0 && filteredInstitutionalDocs.length === 0 && (
                                            <Box sx={{ p: 2 }}>
                                                <Typography sx={{ color: ds.textMuted, fontSize: '0.92rem' }}>
                                                    Nenhum documento encontrado para "{searchQuery}".
                                                </Typography>
                                            </Box>
                                        )}
                                    </List>

                                    <Box
                                        onPointerDown={(event) => event.stopPropagation()}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onWheel={(event) => {
                                            const listNode = sidebarListRef.current;
                                            if (!listNode) return;
                                            listNode.scrollTop += event.deltaY;
                                            event.preventDefault();
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            right: 8,
                                            top: 10,
                                            bottom: 10,
                                            width: 22,
                                            borderRadius: '999px',
                                            border: `1px solid ${alpha(theme.palette.text.primary, 0.28)}`,
                                            bgcolor: alpha(theme.palette.background.paper, 0.98),
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            py: 0.35,
                                            boxShadow: `
                                                inset 0 0 0 1px ${alpha(theme.palette.background.paper, 0.92)},
                                                0 3px 9px ${alpha(theme.palette.common.black, 0.07)}
                                            `
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => scrollSidebarBy(-180)}
                                            disabled={sidebarScrollMetrics.scrollTop <= 2}
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                color: alpha(theme.palette.text.primary, 0.5),
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.text.primary, 0.08),
                                                    color: alpha(theme.palette.text.primary, 0.66)
                                                },
                                                '&.Mui-disabled': {
                                                    color: alpha(theme.palette.text.primary, 0.24)
                                                }
                                            }}
                                        >
                                            <KeyboardDoubleArrowUpIcon sx={{ fontSize: 15 }} />
                                        </IconButton>

                                        <Box
                                            ref={sidebarScrollTrackRef}
                                            onPointerDown={(event) => {
                                                if (event.target !== event.currentTarget) return;
                                                event.preventDefault();
                                                event.stopPropagation();
                                                scrollSidebarToTrackPosition(event.clientY);
                                            }}
                                            sx={{
                                                position: 'relative',
                                                flex: 1,
                                                width: '100%',
                                                px: '3px',
                                                py: '6px',
                                                cursor: 'pointer',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 6,
                                                    bottom: 6,
                                                    left: '50%',
                                                    width: 5,
                                                    transform: 'translateX(-50%)',
                                                    borderRadius: '999px',
                                                    bgcolor: alpha(theme.palette.text.primary, 0.17)
                                                }
                                            }}
                                        >
                                            <Box
                                                onPointerDown={startScrollbarThumbDrag}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 4,
                                                    right: 4,
                                                    top: `${sidebarThumbTopPct}%`,
                                                    height: `${sidebarThumbHeightPct}%`,
                                                    borderRadius: '10px',
                                                    border: `1px solid ${alpha(theme.palette.text.primary, 0.3)}`,
                                                    bgcolor: alpha(theme.palette.background.paper, 1),
                                                    boxShadow: `0 1px 5px ${alpha(theme.palette.common.black, 0.18)}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: isScrollbarThumbDragging ? 'grabbing' : 'grab',
                                                    '&:hover': {
                                                        borderColor: alpha(theme.palette.text.primary, 0.4),
                                                        bgcolor: alpha(theme.palette.background.paper, 1)
                                                    }
                                                }}
                                            >
                                                <Stack spacing={0.28} sx={{ width: '70%', opacity: 0.7 }}>
                                                    <Box sx={{ height: 1.2, borderRadius: 8, bgcolor: alpha(theme.palette.text.primary, 0.48) }} />
                                                    <Box sx={{ height: 1.2, borderRadius: 8, bgcolor: alpha(theme.palette.text.primary, 0.48) }} />
                                                    <Box sx={{ height: 1.2, borderRadius: 8, bgcolor: alpha(theme.palette.text.primary, 0.48) }} />
                                                </Stack>
                                            </Box>
                                        </Box>

                                        <IconButton
                                            size="small"
                                            onClick={() => scrollSidebarBy(180)}
                                            disabled={sidebarScrollMetrics.scrollTop >= sidebarScrollMax - 2}
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                color: alpha(theme.palette.text.primary, 0.5),
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.text.primary, 0.08),
                                                    color: alpha(theme.palette.text.primary, 0.66)
                                                },
                                                '&.Mui-disabled': {
                                                    color: alpha(theme.palette.text.primary, 0.24)
                                                }
                                            }}
                                        >
                                            <KeyboardDoubleArrowDownIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Box>
                                </Box>

                            </Stack>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            bgcolor: ds.surface,
                            border: `1px solid ${ds.border}`,
                            borderRadius: ds.cardRadius,
                            p: { xs: 2.4, md: 3.4, lg: 4.3 },
                            boxShadow: '0 14px 38px rgba(24,34,56,0.11)'
                        }}
                    >
                        <Stack
                            direction="column"
                            spacing={1.2}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                        >
                            <Typography
                                sx={{
                                    color: ds.textMuted,
                                    textTransform: 'uppercase',
                                    fontFamily: '"JetBrains Mono", monospace',
                                    letterSpacing: '0.08em',
                                    fontSize: '0.73rem',
                                    fontWeight: 700,
                                    textAlign: { xs: 'left', md: 'center' }
                                }}
                            >
                                {currentPolicy.category === 'governance' ? 'PADRÃO CORPORATIVO' : 'PROTOCOLO DO SITE'} · {Math.max(currentIndex + 1, 1)}/{orderedPolicies.length}
                            </Typography>
                            {desktopNavigationButtons}
                        </Stack>

                        <Typography
                            variant="h1"
                            sx={{
                                mt: { xs: 2.8, md: 3.4 },
                                mb: 2.6,
                                textTransform: 'none',
                                color: ds.textPrimary,
                                fontSize: { xs: '1.85rem', md: '2.3rem', lg: '2.65rem' },
                                lineHeight: 1.16,
                                letterSpacing: '-0.02em',
                                fontWeight: 800
                            }}
                        >
                            {currentPolicy.title}
                        </Typography>

                        <Divider sx={{ borderColor: ds.border, mb: 3 }} />

                        <Box
                            className="governance-reading-content"
                            sx={{
                                color: ds.textSecondary,
                                '& h1': { display: 'none' },
                                '& h2': {
                                    fontSize: { xs: '1.2rem', md: '1.48rem' },
                                    color: ds.textPrimary,
                                    mt: 4.2,
                                    mb: 1.4,
                                    fontWeight: 750,
                                    letterSpacing: '-0.01em',
                                    borderBottom: `1px solid ${ds.border}`,
                                    pb: 0.8
                                },
                                '& h3': {
                                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                                    color: ds.textPrimary,
                                    mt: 3.2,
                                    mb: 1,
                                    fontWeight: 700
                                },
                                '& p': {
                                    fontSize: { xs: '1rem', md: '1.08rem' },
                                    lineHeight: 1.84,
                                    color: ds.textSecondary,
                                    mb: 2.1,
                                    textAlign: 'left'
                                },
                                '& ul, & ol': {
                                    pl: 3,
                                    mb: 2.2
                                },
                                '& li': {
                                    fontSize: { xs: '1rem', md: '1.04rem' },
                                    lineHeight: 1.75,
                                    color: ds.textSecondary,
                                    mb: 1
                                },
                                '& strong, & b': {
                                    color: ds.textPrimary
                                },
                                '& .last-updated': {
                                    fontSize: '0.84rem',
                                    color: ds.textMuted,
                                    letterSpacing: '0.02em',
                                    mb: 2
                                },
                                '& a': {
                                    color: theme.palette.primary.main,
                                    textDecorationColor: alpha(theme.palette.primary.main, 0.45),
                                    textUnderlineOffset: '3px',
                                    '&:hover': {
                                        color: theme.palette.primary.dark,
                                        textDecorationColor: theme.palette.primary.main
                                    }
                                }
                            }}
                        >
                            <Box dangerouslySetInnerHTML={{ __html: repairedPolicyContent }} />
                        </Box>
                        <SignedDocumentActions slug={currentPolicy.slug} />
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default GovernanceReaderPage;







